import { RouteConfig } from 'vue-router';
import { VueConstructor } from 'vue';
import { ProductType } from '@/commons/enums/ProductType';
import { Routes } from '@/router/Routes';
import { EmptyTemplateWithRouterView } from '@/router/templates';
import EnvironmentProducts from '@/env/services/EnvironmentProducts';
import AuthService from '@/modules/user/AuthService';
import CalculatorNavigation from '@/components/calculator/CalculatorNavigation.vue';
import { useCalculatorVersion } from '@/components/calculator/useCalculatorVersion';
import { NavigationGuard } from 'vue-router/types/router';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { DashboardKey } from '@/modules/onboarding/tours/enums/DashboardKey';
import { createRoutesPerProductTypes } from '@/services/CreateRoutesPerProductTypes';
import ModalService from '@/components/fpComponents/services/ModalService/ModalService';
import { FormSteps } from '@/components/calculator/FormSteps';

const SmeComparatorModal = () => import(/* webpackChunkName: "SmeComparatorModal" */ '@/components/calculator/sme/SmeComparatorModal.vue');

const CashSimulationForm = () => import(/* webpackChunkName: "CashSimulationForm" */ '@/components/calculator/cash/CashSimulationForm.vue');
const MortgageSimulationForm = () => import(/* webpackChunkName: "MortgageSimulationForm" */ '@/components/calculator/mortgage/MortgageSimulationForm.vue');
const CashSimulationResults = () => import(/* webpackChunkName: "CashSimulationResults" */ '@/modules/calculator-v25/views/cash/CashSimulationResults.vue');
const MortgageSimulationResults = () => import(/* webpackChunkName: "MortgageSimulationResults" */ '@/modules/calculator-v25/views/mortgage/MortgageSimulationResults.vue');
const CashSimulationFormStep1 = () => import(/* webpackChunkName: "CashSimulationFormSteps" */ '@/components/calculator/cash/CashSimulationFormStep1.vue');
const CashSimulationFormStep2 = () => import(/* webpackChunkName: "CashSimulationFormSteps" */ '@/components/calculator/cash/CashSimulationFormStep2.vue');
const CashSimulationFormStep3 = () => import(/* webpackChunkName: "CashSimulationFormSteps" */ '@/components/calculator/cash/CashSimulationFormStep3.vue');
const SimulationFormStep4 = () => import(/* webpackChunkName: "SimulationFormStep4" */ '@/components/calculator/form/SimulationFormStep4.vue');
const MortgageSimulationFormStep1 = () => import(/* webpackChunkName: "MortgageSimulationFormSteps" */ '@/components/calculator/mortgage/MortgageSimulationFormStep1.vue');
const MortgageSimulationFormStep2 = () => import(/* webpackChunkName: "MortgageSimulationFormSteps" */ '@/components/calculator/mortgage/MortgageSimulationFormStep2.vue');
const MortgageSimulationFormStep3 = () => import(/* webpackChunkName: "MortgageSimulationFormSteps" */ '@/components/calculator/mortgage/MortgageSimulationFormStep3.vue');
type AsyncComponent = () => Promise<any>;

const components: PartialDeep<Record<ProductType, Record<number, AsyncComponent>>> = {
  [ProductType.CASH]: {
    [FormSteps.CREDIT_PARAMETERS]: CashSimulationFormStep1,
    [FormSteps.BORROWERS]: CashSimulationFormStep2,
    [FormSteps.INCOME]: CashSimulationFormStep3,
    [FormSteps.LIABILITIES]: SimulationFormStep4,
  },
  [ProductType.MORTGAGE]: {
    [FormSteps.CREDIT_PARAMETERS]: MortgageSimulationFormStep1,
    [FormSteps.BORROWERS]: MortgageSimulationFormStep2,
    [FormSteps.INCOME]: MortgageSimulationFormStep3,
    [FormSteps.LIABILITIES]: SimulationFormStep4,
  },
};

function createRoute(productType: ProductType): RouteConfig {
  const product = EnvironmentProducts().find(x => x.type === productType)!;
  return {
    path: productType,
    name: `${Routes.NEW_CALCULATOR.REDIRECT(productType)}`,
    redirect: { name: Routes.NEW_CALCULATOR.FORM(productType, FormSteps.CREDIT_PARAMETERS), },
    meta: {
      icon: product.icon,
      title: product.nominative?.female,
      minNavigation: true,
      condition: () => AuthService?.permitSimulation(productType) && useCalculatorVersion().isNewCalculatorAvailable(),
    },
  };
}

// Business ("Firmowa") products have no comparator — the menu entry opens an
// informational modal offering two paths instead of navigating to a calculator.
const openSmeComparatorModal = async(): Promise<void> => {
  const { default: modalComponent } = await SmeComparatorModal();
  ModalService.open(modalComponent, {}, { maxWidth: 720, persistent: true, });
};

function createSmeComparatorMenuRoute(productType: ProductType): RouteConfig {
  const product = EnvironmentProducts().find(x => x.type === productType)!;
  return {
    path: productType,
    redirect: { name: Routes.DASHBOARD, },
    meta: {
      icon: product.icon,
      title: product.nominative?.female,
      minNavigation: true,
      action: openSmeComparatorModal,
    },
  };
}

function createFormRoute(type: ProductType, step: FormSteps): RouteConfig {
  return {
    props: true,
    path: String(step),
    name: Routes.NEW_CALCULATOR.FORM(type, step),
    component: components[type]![step]!,
    meta: {
      stepperValue: step,
    },
  };
};

const calculatorGuardForType = (type: ProductType): NavigationGuard => (to, from, next) => {
  if (!AuthService.isAuthenticatedForRoute) {
    next({ name: Routes.PASSWORD_MANAGEMENT.LOGIN, });
  } else {
    const conditionsByType: Partial<Record<ProductType, boolean>> = {
      [ProductType.MORTGAGE]: Boolean(AuthService.User!.privileges.mortgageLoansDisabled),
      [ProductType.CASH]: Boolean(AuthService.User!.privileges.cashLoansDisabled),
    };
    if (conditionsByType[type] && useCalculatorVersion().isNewCalculatorAvailable()) {
      next({ name: Routes.DASHBOARD, });
    } else {
      next();
    }
  }
};

const CalculatorRoutes: RouteConfig[] = [
  {
    path: 'calculator-2',
    component: {
      template: EmptyTemplateWithRouterView(),
    },
    children: [
      ...createRoutesPerProductTypes([ProductType.MORTGAGE, ProductType.CASH,], createRoute),
      ...createRoutesPerProductTypes([ProductType.SME,], createSmeComparatorMenuRoute),
    ],
    meta: {
      icon: 'mdi-calculator-variant-outline',
      title: () => I18NGetter().PlatformSection.calculator_v2,
      key: DashboardKey.COMPARISON_TOOL,
      mode: 'lendi',
      condition: () => {
        const privileges = AuthService.User!.privileges;
        return (!privileges.mortgageLoansDisabled || !privileges.cashLoansDisabled) &&
          useCalculatorVersion().isNewCalculatorAvailable();
      },
    },
  },
  {
    path: ProductType.MORTGAGE,
    components: {
      default: {
        template: EmptyTemplateWithRouterView(),
      },
      navigationBar: CalculatorNavigation as unknown as VueConstructor,
    },
    children: [
      {
        path: '',
        component: MortgageSimulationForm,
        props: true,
        children: [
          createFormRoute(ProductType.MORTGAGE, FormSteps.CREDIT_PARAMETERS),
          createFormRoute(ProductType.MORTGAGE, FormSteps.BORROWERS),
          createFormRoute(ProductType.MORTGAGE, FormSteps.INCOME),
          createFormRoute(ProductType.MORTGAGE, FormSteps.LIABILITIES),
        ],
        beforeEnter: calculatorGuardForType(ProductType.MORTGAGE),
      },
      {
        path: 'calculator-summary/:simulationId',
        name: Routes.NEW_CALCULATOR.SUMMARY(ProductType.MORTGAGE),
        component: MortgageSimulationResults,
        beforeEnter: calculatorGuardForType(ProductType.MORTGAGE),
      },
    ],
  },
  {
    path: ProductType.CASH,
    components: {
      default: {
        template: EmptyTemplateWithRouterView(),
      },
      navigationBar: CalculatorNavigation as unknown as VueConstructor,
    },
    children: [
      {
        path: '',
        component: CashSimulationForm,
        props: true,
        children: [
          createFormRoute(ProductType.CASH, FormSteps.CREDIT_PARAMETERS),
          createFormRoute(ProductType.CASH, FormSteps.BORROWERS),
          createFormRoute(ProductType.CASH, FormSteps.INCOME),
          createFormRoute(ProductType.CASH, FormSteps.LIABILITIES),
        ],
        beforeEnter: calculatorGuardForType(ProductType.CASH),
      },
      {
        path: 'calculator-summary/:simulationId',
        name: Routes.NEW_CALCULATOR.SUMMARY(ProductType.CASH),
        component: CashSimulationResults,
        beforeEnter: calculatorGuardForType(ProductType.CASH),
      },
    ],
  },
];

export default CalculatorRoutes;
